/**
 * Utility functions for detecting and categorizing error types
 */

import { NetworkErrorPattern } from '../constants/networkErrorPatterns';

/**
 * Detects if an error is network-related (CORS, connectivity issues, etc.)
 * Used for retry logic and user-facing error messages across authentication
 * flows AND the maintenance probe forwarder. False positives flip healthy
 * users into the maintenance state, so the matcher uses three pattern
 * tiers chosen for the actual shape each pattern takes in real errors:
 *
 * - **Exact** (`Failed to fetch`, `Load failed`, `Network request failed`,
 *   `Network Error`): real fetch / axios transport errors emit these as
 *   the entire `error.message`. Matching them as substrings would let an
 *   app-logic error like `Failed to fetch property X from cache` flip
 *   maintenance state, which is the regression this tightening prevents.
 *
 * - **Prefix** (`net::ERR_`, `NetworkError`): Chromium's `net::ERR_*`
 *   codes are message prefixes; Firefox's network errors look like
 *   `NetworkError when attempting to fetch resource`.
 *
 * - **Word-substring** (`CORS`): real CORS errors look like
 *   `Access to fetch at X has been blocked by CORS policy`, so the
 *   pattern legitimately appears mid-message. The `\bCORS\b` regex
 *   prevents matching unrelated tokens like `record_corset`.
 *
 * @param error - Error object, string message, or unknown error type
 * @returns true if the error appears to be a real network failure
 *
 * @example
 * ```typescript
 * if (isNetworkError(error)) {
 *   // Retry with exponential backoff
 * }
 * ```
 */

/** Patterns that should match the entire (lower-cased, trimmed) message. */
const EXACT_MESSAGE_PATTERNS: readonly string[] = [
  NetworkErrorPattern.FAILED_TO_FETCH.toLowerCase(),
  NetworkErrorPattern.LOAD_FAILED.toLowerCase(),
  NetworkErrorPattern.NETWORK_REQUEST_FAILED.toLowerCase(),
  NetworkErrorPattern.AXIOS_NETWORK_ERROR.toLowerCase(),
];

/** Patterns that should match at the START of the message. */
const PREFIX_MESSAGE_PATTERNS: readonly string[] = [
  NetworkErrorPattern.NET_ERR.toLowerCase(),
  NetworkErrorPattern.NETWORK_ERROR.toLowerCase(),
];

/** Word-boundary substring matcher. CORS appears mid-message in real errors. */
const CORS_RE = /\bcors\b/i;

/** Axios + node error codes that uniformly indicate a network/transport failure. */
const NETWORK_ERROR_CODES: ReadonlySet<string> = new Set([
  'ECONNABORTED',
  'ENOTFOUND',
  'ETIMEDOUT',
  'ECONNRESET',
  'ERR_NETWORK',
]);

function messageMatchesNetworkPattern(value?: string): boolean {
  if (!value) return false;
  const normalized = value.toLowerCase().trim();
  if (EXACT_MESSAGE_PATTERNS.includes(normalized)) return true;
  if (PREFIX_MESSAGE_PATTERNS.some(prefix => normalized.startsWith(prefix))) return true;
  return CORS_RE.test(value);
}

export function isNetworkError(error: unknown): boolean {
  // Handle string errors
  if (typeof error === 'string') {
    return messageMatchesNetworkPattern(error);
  }

  // Handle Error instances. axios attaches `code` to Error instances (it
  // extends Error via AxiosError), so the code check must run for Errors
  // too, not just plain objects. Without this, an axios ERR_NETWORK or
  // ECONNABORTED reaching queryCacheOnError would miss the network-error
  // probe forwarding because the message-pattern fallback doesn't cover
  // axios's terse "network" / "timeout of XXXms exceeded" messages.
  if (error instanceof Error) {
    const errWithCode = error as Error & { code?: string };
    if (errWithCode.code && NETWORK_ERROR_CODES.has(errWithCode.code)) {
      return true;
    }
    if ((error.name as NetworkErrorPattern) === NetworkErrorPattern.NETWORK_ERROR) {
      return true;
    }
    return messageMatchesNetworkPattern(error.message);
  }

  if (error && typeof error === 'object') {
    const err = error as {
      message?: string;
      code?: string;
      name?: string;
    };

    if (err.code && NETWORK_ERROR_CODES.has(err.code)) {
      return true;
    }

    if ((err.name as NetworkErrorPattern) === NetworkErrorPattern.NETWORK_ERROR) {
      return true;
    }

    return messageMatchesNetworkPattern(err.message);
  }

  return false;
}

/** The words axios gives a request the browser cancelled, under the code a timeout also takes. */
const BROWSER_ABORT_RE = /\baborted\b/i;

/**
 * A request axios stopped because its `timeout` ran out before any answer came: ETIMEDOUT (under
 * `clarifyTimeoutError`), or ECONNABORTED ("timeout of 30000ms exceeded") unless its message is the
 * one axios gives a request the browser cancelled ("Request aborted"), which takes the same code.
 * `isNetworkError` counts both, and neither carries a status; on a device that is online, a timeout
 * means the server took longer than the app waits, not that the connection dropped.
 */
export function isRequestTimeout(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code === 'ETIMEDOUT') return true;
  return (
    code === 'ECONNABORTED' && !(typeof message === 'string' && BROWSER_ABORT_RE.test(message))
  );
}

/**
 * Extract HTTP status code from various error shapes (Axios, fetch, direct).
 * Returns undefined if no status code can be extracted.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  // Nested in response object (Axios pattern)
  const responseStatus = (error as { response?: { status?: number } }).response?.status;
  if (typeof responseStatus === 'number') return responseStatus;

  // Direct status property (fetch / custom errors)
  const directStatus = (error as { status?: number }).status;
  if (typeof directStatus === 'number') return directStatus;

  return undefined;
}

/**
 * Discriminated states a failed entity/data load can resolve to, for screens that
 * render a recoverable error UI (community detail, space analytics, welcome, …).
 */
export type LoadErrorKind = 'offline' | 'auth' | 'server' | 'notfound';

/** 4xx answers that are about this one request, not about the thing it asked for. */
const TRANSIENT_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 429]);

/**
 * Classify a failed data-load error into a renderable state.
 *
 * Precedence: a *definitive HTTP status* wins first. If the server responded, the
 * device was online for that request, so we trust the status over `navigator.onLine`
 * (a known false-negative in some native WebViews, e.g. Capacitor Android — where
 * offline-first would mask a real 401/500 as "offline" and trap the user on a Retry
 * loop). 401 → auth, >=500 → server, any other status (404/403/…) → notfound. Only a
 * *status-less* failure falls back to the offline check (a transport error, or the
 * browser reporting offline); an unknown error while online is treated as notfound.
 *
 * Two 4xx statuses are `server` too: 408 (the server gave up waiting for the request) and
 * 429 (too many requests). Neither says anything about the thing being loaded, and the same
 * read can succeed a moment later, so they belong with the failures a retry can pass, never
 * with "not found". `server` therefore means "the server could not answer this time", and
 * its copy has to stay true of a timeout and of a rate limit as well as of a 5xx.
 */
export function classifyLoadError(error: unknown): LoadErrorKind {
  const status = getErrorStatus(error);
  if (status === 401) return 'auth';
  if (status !== undefined && (status >= 500 || TRANSIENT_CLIENT_STATUSES.has(status))) {
    return 'server';
  }
  if (status !== undefined) return 'notfound';
  if (isNetworkError(error) || !navigator.onLine) return 'offline';
  return 'notfound';
}

/**
 * Build the status-first load-error copy table for a named entity, in the product's own
 * spelling of it ("Space", "post"). `offline`/`server` are entity-agnostic; `auth`/`notfound`
 * name the entity. Single source of truth so the screens that render a
 * recoverable load error can't drift — they previously kept near-identical
 * hand-authored maps whose `server` copy had already diverged ("in a moment"
 * vs "later"; reconciled here to "in a moment").
 *
 * The welcome flow keeps its own richer `WELCOME_ERROR_DESCRIPTORS` table
 * (copy keys + a routed action for i18n) — different shape and voice.
 */
export function buildLoadErrorCopy(
  noun: string,
): Record<LoadErrorKind, { title: string; description: string }> {
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  return {
    offline: {
      title: 'No connection',
      description: 'Please check your internet connection and try again.',
    },
    auth: {
      title: 'Sign in required',
      description: `Please sign in to view this ${noun}.`,
    },
    server: {
      title: 'Something went wrong',
      description: 'Please try again in a moment.',
    },
    notfound: {
      title: `${Noun} not found`,
      description: `This ${noun} may have been removed or the link is incorrect.`,
    },
  };
}

/**
 * The `detail` an API error carries, whatever shape it is in.
 *
 * ONE guard-and-cast for the three readers below, which each used to spell it out verbatim. The
 * return is `unknown` on purpose: each reader decides what it can turn into a sentence (a string,
 * a validation LIST, a moderation OBJECT) and returns `undefined` for anything else, so nothing
 * here hands a caller a value it has not checked.
 */
function readResponseDetail(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
}

/**
 * Extract the FastAPI-shaped `detail` string from an Axios error response.
 *
 * Returns `undefined` for non-Axios shapes or when `detail` isn't a non-empty
 * string (e.g. structured 403 payloads — `parseQuestionAccessError` in
 * `questionAccessErrors.ts` handles those). Call sites typically:
 *
 * ```ts
 * const detail = getErrorDetail(err);
 * toast({ title: detail ?? 'Failed to update X', variant: 'destructive' });
 * ```
 *
 * Mirrors the precedent in `useSpaceInvitations.ts` where the cast was inlined;
 * centralising it here gives one provably-correct extraction site and lets new
 * mutation hooks adopt the pattern with a one-line import.
 */
export function getErrorDetail(error: unknown): string | undefined {
  const detail = readResponseDetail(error);
  // Guard empty strings too: callers do `getErrorDetail(err) ?? 'fallback'`, and
  // `'' ?? x` yields `''` (nullish coalescing keeps the empty string), so an
  // empty `detail` would surface a blank toast instead of the fallback.
  return typeof detail === 'string' && detail.length > 0 ? detail : undefined;
}

/** One pydantic validation error, as FastAPI puts it in the `detail` LIST of a 422. */
interface ValidationDetailEntry {
  loc?: unknown;
  msg?: unknown;
}

/**
 * The field a validation entry is about, read off pydantic's `loc` path.
 *
 * The leading scope segment (`body`, `query`, `path`) says where the field came from, which the
 * admin reading the toast already knows, so it is dropped. Numeric segments (list indexes) are
 * dropped with it, leaving the field name a human recognises from the form.
 */
function validationFieldName(loc: unknown): string | undefined {
  if (!Array.isArray(loc)) return undefined;
  // Only the LEADING segment is a scope. Stripping the word wherever it appears would also erase a
  // field that happens to be called `body`, which pydantic reports as `['body', 'body']`.
  const [scope, ...rest] = loc as unknown[];
  const withoutScope =
    scope === 'body' || scope === 'query' || scope === 'path' ? rest : (loc as unknown[]);
  const parts = withoutScope.filter((part): part is string => typeof part === 'string');
  return parts.length > 0 ? parts.join('.') : undefined;
}

const VALUE_ERROR_PREFIX = 'Value error, ';

/** The sentence an entry's `msg` carries; `undefined` for a non-string or an empty one. */
function validationMessageText(msg: unknown): string | undefined {
  if (typeof msg !== 'string') return undefined;

  // Pydantic prefixes every custom `ValueError` with "Value error, "; what follows is the sentence
  // the backend actually wrote for this case, which is the part worth showing.
  const text = (
    msg.startsWith(VALUE_ERROR_PREFIX) ? msg.slice(VALUE_ERROR_PREFIX.length) : msg
  ).trim();
  // Emptiness is judged AFTER the prefix comes off, not before: `msg` is non-empty right up until
  // it does, and what is left would reach the admin as "notification_topic_id: " with no sentence
  // behind the colon.
  return text.length > 0 ? text : undefined;
}

function validationEntryMessage(entry: unknown): string | undefined {
  if (!entry || typeof entry !== 'object') return undefined;
  const { loc, msg } = entry as ValidationDetailEntry;
  const text = validationMessageText(msg);
  if (text === undefined) return undefined;
  const field = validationFieldName(loc);
  return field ? `${field}: ${text}` : text;
}

/**
 * Whether a 422's `detail` LIST has an entry about `field`: one whose `loc` path names it at any
 * depth (`['body', 'external_event_urls']`, or an item of it, `['body', 'external_event_urls', 0]`).
 * Matched on `loc` only, never on `msg`: a form shows its own copy for the field, so a change in
 * the backend's wording can neither break the match nor reach the screen.
 */
export function hasValidationErrorFor(error: unknown, field: string): boolean {
  const detail = readResponseDetail(error);
  return (
    Array.isArray(detail) &&
    detail.some((entry: unknown) => {
      const loc = (entry as ValidationDetailEntry | null | undefined)?.loc;
      return Array.isArray(loc) && loc.includes(field);
    })
  );
}

/**
 * Turn a 422's `detail` LIST into one readable sentence.
 *
 * Unlike `getErrorDetail` (a string `detail`), a request pydantic refuses answers with a list of
 * entries, so a caller that only handles strings falls through to its generic copy and the admin
 * is told "Please try again." about a body that will never be accepted. Returns `undefined` when
 * the shape is not a validation list, so callers keep their own fallback.
 */
export function getValidationDetailMessage(error: unknown): string | undefined {
  const detail = readResponseDetail(error);
  if (!Array.isArray(detail)) return undefined;

  const messages = detail
    .map(validationEntryMessage)
    .filter((message): message is string => message !== undefined);

  return messages.length > 0 ? messages.join('; ') : undefined;
}

/**
 * A 422's `detail` LIST as one sentence per field, for a form that shows each refusal under its own
 * control: the field an entry is about (its `loc`, read as `validationFieldName` reads it) to the
 * sentence of the FIRST entry about it, without pydantic's prefix. An entry that names no field or
 * carries no sentence is left out, and any other shape (a string `detail`, an APIError envelope)
 * gives an empty map, so a caller reads `messages[field]` without a guard.
 */
export function getValidationMessagesByField(error: unknown): Record<string, string> {
  const detail = readResponseDetail(error);
  if (!Array.isArray(detail)) return {};

  const messages = new Map<string, string>();
  for (const entry of detail as unknown[]) {
    if (!entry || typeof entry !== 'object') continue;
    const { loc, msg } = entry as ValidationDetailEntry;
    const field = validationFieldName(loc);
    const text = validationMessageText(msg);
    if (field !== undefined && text !== undefined && !messages.has(field)) {
      messages.set(field, text);
    }
  }
  return Object.fromEntries(messages);
}

/**
 * `error_code` the backend sends inside a 422 `detail` object when user-generated
 * content is blocked by moderation on create paths (question create, chat send).
 * Contract: `detail = { message, error_code: 'CONTENT_REJECTED', status: 422 }`.
 */
export const CONTENT_REJECTED_ERROR_CODE = 'CONTENT_REJECTED';

/**
 * Extract the human-readable moderation message from a CONTENT_REJECTED 422.
 *
 * Unlike `getErrorDetail` (which reads a *string* `detail`), moderation rejections
 * ship a structured `detail` object — same axios shape parsed by
 * `parseQuestionAccessError`. Returns the `message` only when `error_code` matches
 * and `message` is a non-empty string; otherwise `undefined` so callers fall back
 * to their generic failure copy.
 */
export function getContentRejectedMessage(error: unknown): string | undefined {
  const detail = readResponseDetail(error);
  if (!detail || typeof detail !== 'object') return undefined;

  const { error_code, message } = detail as { error_code?: unknown; message?: unknown };
  if (error_code !== CONTENT_REJECTED_ERROR_CODE) return undefined;

  return typeof message === 'string' && message.length > 0 ? message : undefined;
}

/** Backend refusal of a post asked on someone's behalf whose every Space target is held
 * (author only PENDING there). 400, APIError envelope, `details.held_space_ids`. */
export const ASK_ON_BEHALF_HELD_ONLY_ERROR_CODE = 'ASK_ON_BEHALF_HELD_ONLY';

/** Backend refusal of a write that would leave a post in more Spaces than the cap allows
 * (`MAX_QUESTION_SPACES`, served as `/me.max_question_spaces`; live and held links both count,
 * and only growth is refused). 422, APIError envelope, `details.offending_space_ids` and
 * `details.max_question_spaces`. Raised by the share, and by create and edit (TECH-683). */
export const QUESTION_SPACE_CAP_ERROR_CODE = 'QUESTION_SPACE_CAP';

/** Backend refusal of a join by a caller whose earlier request is still waiting on a host.
 * 409, APIError envelope. What a member is told for it: `joinFailureToast` (membershipFailureCopy.ts). */
export const JOIN_REQUEST_PENDING_ERROR_CODE = 'JOIN_REQUEST_PENDING';

/** Backend refusal of a PIN sent to resolve a waiting request that, in that moment, stopped
 * waiting (declined, or gone). 409, APIError envelope. */
export const JOIN_REQUEST_NOT_PENDING_ERROR_CODE = 'JOIN_REQUEST_NOT_PENDING';

/** Backend refusal of a join by a caller who is already a member. 409, APIError envelope. A
 * backend that sends no code for it answers a waiting requester with the same plain 409, so a
 * 409 with no code is never read as this one (see `joinFailureToast`). */
export const JOIN_ALREADY_MEMBER_ERROR_CODE = 'JOIN_ALREADY_MEMBER';

/** Backend refusal of a join to an unlisted Space for want of a valid invite link. 403,
 * APIError envelope. Checked before the PIN, so a join that carried a PIN can get it. */
export const JOIN_INVITE_LINK_REQUIRED_ERROR_CODE = 'JOIN_INVITE_LINK_REQUIRED';

/** Backend refusal of a join whose PIN is wrong. 403, APIError envelope. */
export const JOIN_PIN_INVALID_ERROR_CODE = 'JOIN_PIN_INVALID';

/** Backend lockout after too many PIN attempts (TECH-663). 429, APIError envelope; lasts 15 minutes. */
export const JOIN_PIN_RATE_LIMITED_ERROR_CODE = 'JOIN_PIN_RATE_LIMITED';

/** Backend refusal of a config save or Revert that started from an old version: another save has
 * moved the row on since the page read it. 409, APIError envelope, `details.version` (the version
 * the row holds now) and `details.updated_by` (who saved it, as a user id; null for a migration).
 * Read it with `getStaleConfigVersion`. */
export const CONFIG_VERSION_STALE_ERROR_CODE = 'CONFIG_VERSION_STALE';

/** Backend refusal of a Revert whose field no longer holds the value the change wrote: someone
 * changed it outside History (by hand, or a release going back), so the row the page shows can be
 * out of date even at the version it holds. 409, APIError envelope, whose message says to save the
 * wanted value instead. */
export const CONFIG_REVERT_VALUE_CHANGED_ERROR_CODE = 'CONFIG_REVERT_VALUE_CHANGED';

/** Backend refusal of a settings save or Revert while the settings row is missing: notifications
 * run on the built-in values, and saving stays off until a person restores the row, so trying
 * again cannot help. 500, APIError envelope. */
export const NOTIFICATION_SETTINGS_MISSING_ERROR_CODE = 'NOTIFICATION_SETTINGS_MISSING';

/** Backend answer for a settings read or write that a lock wait or a deadlock stopped: the server
 * is up and asks to be tried again in a moment. 503, APIError envelope, `Retry-After`. */
export const NOTIFICATION_SETTINGS_BUSY_ERROR_CODE = 'NOTIFICATION_SETTINGS_BUSY';

/** The same answer for a notification template's read or write. */
export const NOTIFICATION_TEMPLATE_BUSY_ERROR_CODE = 'NOTIFICATION_TEMPLATE_BUSY';

/** Backend refusal to delete a template the notification record holds items of: it can only be
 * switched off. 409, APIError envelope, whose message is written for the API's callers. */
export const TEMPLATE_HAS_HISTORY_ERROR_CODE = 'TEMPLATE_HAS_HISTORY';

/** The live poll's `/open` with no guest-token secret on the server (fail closed, TECH-867): the poll is read only
 * and guests are asked to sign in. 503 by design, APIError envelope; the server is up. */
export const LIVE_GUESTS_UNAVAILABLE_ERROR_CODE = 'LIVE_GUESTS_UNAVAILABLE';

/** Backend answer for any request a busy database stopped: a wait for a pooled connection or for
 * a lock ran past its bound, so the request did nothing and the server is up and asks to be tried
 * again. 503, APIError envelope, `Retry-After`. */
export const DATABASE_BUSY_ERROR_CODE = 'DATABASE_BUSY';

/** The codes of the backend's busy 503: the two notification config pages' `RetryLaterError` codes
 * and `DATABASE_BUSY`, the answer of every other request; and the other 503s it sends by design while
 * up (`LIVE_GUESTS_UNAVAILABLE`). None is outage evidence. The external fetch answers'
 * `EXTERNAL_UNAVAILABLE`, a 503 the backend also sends by design, is not one of them. */
const RETRY_LATER_ERROR_CODES: ReadonlySet<string> = new Set([
  NOTIFICATION_SETTINGS_BUSY_ERROR_CODE,
  NOTIFICATION_TEMPLATE_BUSY_ERROR_CODE,
  LIVE_GUESTS_UNAVAILABLE_ERROR_CODE,
  DATABASE_BUSY_ERROR_CODE,
]);

/**
 * The `detail` OBJECT of the backend's shared APIError envelope
 * (`detail: { message, error_code, status, details? }`). `undefined` for a string `detail`, a
 * validation LIST or a non-Axios value.
 */
function readEnvelope(error: unknown): Record<string, unknown> | undefined {
  const detail = readResponseDetail(error);
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)
    : undefined;
}

/** The envelope's `details` object, where a refusal names what it is about; `undefined` without one. */
function readEnvelopeDetails(error: unknown): Record<string, unknown> | undefined {
  const details = readEnvelope(error)?.details;
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : undefined;
}

/** The envelope's `details` object (`detail.details`), for a caller that reads a refusal's payload. */
export function getErrorDetails(error: unknown): Record<string, unknown> | undefined {
  return readEnvelopeDetails(error);
}

/** The non-empty strings of a list; empty for anything that is not a list. */
const nonEmptyStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
    : [];

/**
 * The machine-readable `error_code` from the backend's shared APIError envelope
 * (`detail: { message, error_code, status, details? }`), e.g. `FORBIDDEN` for any 403: a post
 * into a Space the caller does not belong to, an edit of a post that is no longer theirs, a
 * custom slug from a non-admin; the code alone does not tell them apart. `undefined` for a
 * string `detail`, a validation LIST, an empty code or a non-Axios value. For analytics; never
 * shown to people.
 */
export function getErrorCode(error: unknown): string | undefined {
  const errorCode = readEnvelope(error)?.error_code;
  return typeof errorCode === 'string' && errorCode.length > 0 ? errorCode : undefined;
}

/**
 * A 503 the backend sends by design (its `RetryLaterError`, or `DATABASE_BUSY` for a request a
 * busy database stopped): a read or a write that a lock wait, a deadlock or a pool wait stopped,
 * answered in the APIError envelope with a busy code and `Retry-After`. The
 * server is up and answering, so it is no sign of an outage: neither maintenance forwarder (the API
 * client's response interceptor, `queryCacheOnError`) sends it to `maybeProbe`. A new by-design 503
 * code joins `RETRY_LATER_ERROR_CODES` when it comes as this envelope's `error_code`; one sent as
 * `detail.code` (the external fetch answers' `EXTERNAL_UNAVAILABLE`) needs this predicate to read
 * that shape first. Any other 503 stays evidence for the detector.
 */
export function isRetryLaterAnswer(error: unknown): boolean {
  if (getErrorStatus(error) !== 503) return false;
  const code = getErrorCode(error);
  return code !== undefined && RETRY_LATER_ERROR_CODES.has(code);
}

/**
 * The `message` of the APIError envelope (the one `getErrorCode` reads): the sentence the server
 * wrote for its refusal, such as a Revert another change has overtaken, for a caller that shows it.
 * `undefined` for a string `detail`, a validation LIST, an empty message or a non-Axios value, so
 * the caller keeps its own fallback.
 */
export function getEnvelopeMessage(error: unknown): string | undefined {
  const message = readEnvelope(error)?.message;
  return typeof message === 'string' && message.length > 0 ? message : undefined;
}

/** `text` when it holds more than whitespace; `undefined` otherwise, so a toast is never blank. */
const sentenceOrUndefined = (text: string | undefined): string | undefined =>
  text !== undefined && text.trim().length > 0 ? text : undefined;

/**
 * The sentence to show a person for a failed request: a string `detail`, else the message of a
 * busy 503 (`isRetryLaterAnswer`), else `fallback`. Always a string.
 *
 * Why only the busy answer's envelope: the backend writes envelope sentences for its API's callers,
 * not for members ("Invalid token", "Not authorized to modify this resource", "Space not found
 * (ID: ...)"), and none of them is approved copy. The busy answer is the exception: its message
 * ("The server couldn't answer just now. Try again in a moment.") is written for people and names
 * the next step. Every other envelope, a validation LIST (its entries name internal fields;
 * `getValidationDetailMessage` is for the screens that want them), a blank detail and a non-Axios
 * value get `fallback`.
 *
 * The `detail` itself is never returned, because an envelope OBJECT or a validation LIST handed to
 * a toast reaches React as a child and throws ("Objects are not valid as a React child"). Status
 * branches stay with the caller, which passes the fallback that suits the status.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  return (
    sentenceOrUndefined(getErrorDetail(error)) ??
    sentenceOrUndefined(isRetryLaterAnswer(error) ? getEnvelopeMessage(error) : undefined) ??
    fallback
  );
}

/** What a `CONFIG_VERSION_STALE` refusal says, as `getStaleConfigVersion` reads it. */
export interface StaleConfigVersion {
  /** The version the row holds now: the one the next save starts from. */
  version: number;
  /** Who made the save that moved it on, as a user id; null when a migration made it. */
  updatedBy: string | null;
}

/**
 * What a `CONFIG_VERSION_STALE` refusal (409) says: the version the row holds now, and who moved
 * it there. The code identifies the refusal, and `details.version` must be one a save can send
 * back, a whole number from 0. `undefined` for any other error, and for a stale refusal without
 * such a version. `updatedBy` is null when the details name no one.
 */
export function getStaleConfigVersion(error: unknown): StaleConfigVersion | undefined {
  if (getErrorCode(error) !== CONFIG_VERSION_STALE_ERROR_CODE) return undefined;
  const details = readEnvelopeDetails(error);
  const version = details?.version;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 0) return undefined;
  const updatedBy = details?.updated_by;
  return {
    version,
    updatedBy: typeof updatedBy === 'string' && updatedBy.length > 0 ? updatedBy : null,
  };
}

/**
 * The non-empty string ids listed under `details[key]` of the APIError envelope (the one
 * `getErrorCode` reads). Empty for any other shape, so a caller can branch on the length alone.
 */
/**
 * The one Space a refusal names under `details.space_id` (the poll vote's join refusals,
 * `POLL_PIN_REQUIRED` and `POLL_CANNOT_JOIN`); null when it names none.
 */
export function getDetailsSpaceId(error: unknown): string | null {
  const id = readEnvelopeDetails(error)?.space_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

function readSpaceIdsFromDetails(
  error: unknown,
  key: 'forbidden_space_ids' | 'missing_space_ids' | 'forbidden_event_ids',
): string[] {
  return nonEmptyStrings(readEnvelopeDetails(error)?.[key]);
}

/**
 * The Space ids a 403 from the Space access check names under `details.forbidden_space_ids`:
 * Spaces in the request the caller can no longer post in (a declined or withdrawn request, a
 * removed membership). Empty for any other shape.
 */
export function getForbiddenSpaceIds(error: unknown): string[] {
  return readSpaceIdsFromDetails(error, 'forbidden_space_ids');
}

/**
 * The Space ids a 404 from the same check names under `details.missing_space_ids`: Spaces in
 * the request that are gone for the caller (deleted, archived, or the caller was blocked
 * there). Empty for any other shape, a 404 about the post itself included.
 */
export function getMissingSpaceIds(error: unknown): string[] {
  return readSpaceIdsFromDetails(error, 'missing_space_ids');
}

/**
 * The Event ids a 403 `POLL_CREATE_FORBIDDEN` names under `details.forbidden_event_ids` (TECH-197
 * CONTRACT § 4.0): Events in a poll's create the caller may not create a poll in. Empty otherwise.
 */
export function getForbiddenEventIds(error: unknown): string[] {
  return readSpaceIdsFromDetails(error, 'forbidden_event_ids');
}

/** What a `QUESTION_SPACE_CAP` refusal says, as `getQuestionSpaceCapRefusal` reads it. */
export interface SpaceCapRefusal {
  maxSpaces: number | undefined;
  offendingSpaceIds: string[];
}

/**
 * What a `QUESTION_SPACE_CAP` refusal (422) says: the cap the server applied
 * (`max_question_spaces`) and the Spaces past it (`offending_space_ids`: the last of the added
 * Spaces, in request order). The one reader of that refusal, for a post's create and edit and for
 * its share alike. `null` for any other error.
 *
 * The refusal is the APIError with `error_code` QUESTION_SPACE_CAP. The status alone does not
 * identify it: the share also answers 422 for an anonymous post, with a string `detail`. A
 * backend from before TECH-683 sent the share's refusal without the envelope, as a bare `detail`
 * with the ids at its top, so a 422 with no code whose `detail` lists `offending_space_ids` is
 * read as this refusal too.
 *
 * Each field is read from the envelope's `details`, else from the top of `detail`, where the
 * backend mirrors `offending_space_ids` for the share clients built before the envelope.
 * `maxSpaces` is `undefined` when neither carries a usable number, and `offendingSpaceIds` is
 * empty when they name none, so a caller falls back on what it knows.
 */
export function getQuestionSpaceCapRefusal(error: unknown): SpaceCapRefusal | null {
  const envelope = readEnvelope(error);
  if (!envelope) return null;
  const errorCode = getErrorCode(error);
  const isCapRefusal =
    errorCode === QUESTION_SPACE_CAP_ERROR_CODE ||
    (errorCode === undefined &&
      getErrorStatus(error) === 422 &&
      Array.isArray(envelope.offending_space_ids));
  if (!isCapRefusal) return null;

  const details = readEnvelopeDetails(error);
  const field = (key: 'max_question_spaces' | 'offending_space_ids'): unknown =>
    details?.[key] ?? envelope[key];
  const maxSpaces = field('max_question_spaces');
  return {
    maxSpaces:
      typeof maxSpaces === 'number' && Number.isInteger(maxSpaces) && maxSpaces > 0
        ? maxSpaces
        : undefined,
    offendingSpaceIds: nonEmptyStrings(field('offending_space_ids')),
  };
}
